import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { User } from '@client/components/_ui/display/User';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { FallbackModelChip } from '@client/components/compliance-audit/FallbackModelChip';
import { useComplianceAuditFilterConfig } from '@client/components/compliance-audit/useComplianceAuditFilterConfig';
import { buildInitialWhereFromFilters, FilterBar, useFilterBuilderWithUrl } from '@client/components/filters';
import { useCanManageComplianceAudit } from '@client/hooks/business/compliance-audit/useCanManageComplianceAudit';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Tag } from 'antd';
import { Bug, StickyNoteOff } from 'lucide-react';
import { useState } from 'react';
import {
    acceptDeepLinkFilters,
    ComplianceAuditSearchSchema,
    discardRetiredRestoredState,
} from './compliance-audit-search-filters';

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/overview/search')({
    staticData: {
        metadata: {
            title: 'Compliance audit',
        },
        ui: {
            menuDescription: 'AI-assisted file audits for broker compliance',
        },
    },
    validateSearch: zodValidator(ComplianceAuditSearchSchema),
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplianceAuditList />
        </PageController>
    );
}

type QueryParams = RouterInputs['complianceAudit']['listComplianceAudits'];
type ComplianceAuditRow = RouterOutputs['complianceAudit']['listComplianceAudits']['rows'][number];

const columnHelper = createColumnHelper<ComplianceAuditRow, QueryParams['orderBy']>();

function ComplianceAuditList() {
    const orgId = useOrgId();
    const canManage = useCanManageComplianceAudit();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const isUIDebugInfoEnabled = useIsUIDebugInfoEnabled();
    const searchParams = Route.useSearch();

    const { config: filterConfig, ready: filterConfigReady } = useComplianceAuditFilterConfig();
    const deepLinkFilters = acceptDeepLinkFilters(filterConfig, searchParams['compliance-audit-filters']);

    // Once per mount and before the path-state hooks below read the store: restored state
    // bypasses the URL check above, so a retired status value saved before a deploy is
    // discarded here rather than sent as the first list request.
    const { pathname } = useLocation();
    useState(() =>
        discardRetiredRestoredState(filterConfig, pathname, {
            filters: 'compliance-audit-filters',
            query: 'query-params',
        }),
    );

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        orderBy: { createdAt: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        // Seeded from the URL so a deep link's first query is already filtered —
        // no unfiltered flash followed by a refetch.
        where: buildInitialWhereFromFilters(filterConfig, deepLinkFilters),
    });

    // Search and filters narrow the same list conjunctively: `buildWhereClause` ANDs
    // every active filter, so adding one can only ever shrink the result set.
    const filterState = useFilterBuilderWithUrl(filterConfig, 'compliance-audit-filters', deepLinkFilters, (where) =>
        setInput((prev) => ({ ...prev, where, offset: 0 })),
    );

    const { data, isPending, error, refetch } = trpc.complianceAudit.listComplianceAudits.useQuery(input, {
        enabled: filterConfigReady,
    });

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No compliance audits yet' },
        columnVisibility: {
            enabled: true,
            persistKey: 'compliance-audit-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        columns: [
            columnHelper.accessor('insuredName', {
                id: 'insuredName',
                header: 'Insured',
                sortable: 'insuredName',
                cell({ row }) {
                    return row.insuredName ?? '—';
                },
            }),
            columnHelper.accessor('policyNumber', {
                id: 'policyNumber',
                header: 'Policy number',
                sortable: 'policyNumber',
                cell({ row }) {
                    return row.policyNumber ?? '—';
                },
            }),
            columnHelper.display({
                id: 'owner',
                header: 'Auditor',
                cell: ({ row }) => (
                    <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.photoUrl} />
                ),
            }),
            columnHelper.accessor('brokerName', {
                id: 'broker',
                header: 'Broker',
                sortable: 'brokerName',
                cell({ row }) {
                    return row.brokerName ?? '—';
                },
            }),
            columnHelper.accessor('authorisedRepresentative', {
                id: 'ar',
                header: 'AR',
                sortable: 'authorisedRepresentative',
                cell({ row }) {
                    return row.authorisedRepresentative ?? '—';
                },
            }),
            // CUR-549 — the pinned template that produced this audit: name as text with
            // the version in an outlined Tag, matching the admin-console template list.
            // Display-only (no sort); legacy/hardcoded audits show "—".
            columnHelper.display({
                id: 'template',
                header: 'Template',
                cell({ row }) {
                    const template = row.ComplianceAuditTemplateVersion;
                    if (!template) return '—';
                    return (
                        <span className="inline-flex items-center gap-2">
                            {template.name}
                            <Tag variant="outlined" className="m-0">
                                v{template.version}
                            </Tag>
                        </span>
                    );
                },
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell({ row }) {
                    return (
                        <span className="inline-flex items-center gap-1">
                            <StatusBadge status={row.status} module="complianceAudit" variant="outlined" />
                            {/* CUR-551 — an all-not-applicable audit derives Pass (CUR-1624: no
                                failed step) but was never substantively reviewed; a quiet icon
                                Tag keeps it from reading as a fully-assessed green review. */}
                            {row.noStepAssessed && (
                                // Deliberately icon-only: the StickyNoteOff glyph + tooltip
                                // is an at-a-glance "not assessed" signal that keeps the list
                                // cell scannable, without adding a text label that would
                                // compete with the adjacent StatusBadge. Not an oversight —
                                // the qualifier is intentionally wordless here. The `role`/
                                // `aria-label` (mirroring the tooltip copy) give screen-reader
                                // and keyboard users the same qualifier without visible text.
                                <Tooltip title="No steps assessed — every step was not applicable">
                                    <Tag
                                        variant="outlined"
                                        className="m-0 inline-flex h-5.5 items-center"
                                        icon={<StickyNoteOff className="size-3.5" />}
                                        role="img"
                                        aria-label="Not assessed — every step was not applicable"
                                    />
                                </Tooltip>
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Created on',
                sortable: 'createdAt',
                cell({ row }) {
                    return formatDate(row.createdAt);
                },
            }),
            // CUR-1679 — debug-only: the column exists only in UI debug mode, which is
            // also when the list read carries each row's `fallback` state. Non-hideable,
            // so it shows as soon as debug mode is enabled on an open list: the table's
            // visible-column set is fixed at mount and would leave a hideable one out.
            ...(isUIDebugInfoEnabled
                ? [
                      columnHelper.display({
                          id: 'debug-info',
                          hideable: false,
                          header: (
                              <span className="flex items-center gap-1">
                                  <Bug size={14} className="text-subtle-foreground" />
                                  Debug info
                              </span>
                          ),
                          cell: ({ row }) => <FallbackModelChip level="audit" state={row.fallback?.state} />,
                      }),
                  ]
                : []),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.OpenInNewTab
                        to="/console/$orgId/tools/compliance-audit/details/$complianceAuditId"
                        params={{ orgId, complianceAuditId: row.id }}
                        tooltip="Open in new tab"
                    />
                ),
            }),
        ],
    });

    // `<Outlet />` (the "new audit" drawer) stays mounted so it keeps working even
    // when the list query fails; the failed list surfaces inline in place of the table.
    // The toolbar stays up in that state too — a filter set the server rejects is
    // exactly when the user needs the filter bar to clear it.
    return (
        <>
            <Outlet />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={filterState.filters}
                        whereClause={filterState.whereClause}
                        onAddFilter={filterState.addFilter}
                        onUpdateFilter={filterState.updateFilter}
                        onRemoveFilter={filterState.removeFilter}
                        onClearFilters={filterState.clearFilters}
                    />
                }
                actions={
                    canManage && (
                        <Button.Add.Link
                            to="/console/$orgId/tools/compliance-audit/overview/search/add"
                            params={{ orgId }}
                            variant="primary"
                            label="New audit"
                        />
                    )
                }
            />

            {error ? (
                <InlineQueryError error={error} onRetry={refetch} />
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id }) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/console/$orgId/tools/compliance-audit/details/$complianceAuditId',
                                    params: { orgId, complianceAuditId: id },
                                },
                                e,
                            );
                        },
                    })}
                />
            )}
        </>
    );
}
